import DOMPurify from 'dompurify';

const ALLOWED_TAGS = [
  'a', 'abbr', 'b', 'blockquote', 'br', 'code', 'col', 'colgroup', 'del', 'div',
  'em', 'font', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'i', 'img', 'li', 'ol', 'p',
  'pre', 's', 'span', 'strong', 'sub', 'sup', 'table', 'tbody', 'td', 'tfoot',
  'th', 'thead', 'tr', 'u', 'ul'
];

const ALLOWED_ATTR = [
  'align', 'aria-label', 'class', 'colspan', 'data-border-style',
  'data-header-column', 'data-header-row', 'data-mention-exit',
  'data-original-code', 'data-pmpi-created-table', 'data-pmpi-raw-html',
  'data-pmpi-row-numbers', 'data-pmpi-table', 'data-user-id', 'dir', 'height',
  'alt', 'color', 'face', 'href', 'lang', 'rel', 'rowspan', 'scope', 'size',
  'src', 'start', 'style', 'target', 'title', 'width'
];

const SAFE_CLASSES = new Set([
  'comment-box-image-paste-error', 'mention', 'mention-exit', 'pmpi-code-block',
  'pmpi-created-table', 'pmpi-rich-table'
]);

const BASE_STYLE_PROPERTIES = new Set([
  'background-color', 'border', 'border-bottom', 'border-color', 'border-left',
  'border-radius', 'border-right', 'border-style', 'border-top', 'border-width',
  'color', 'display', 'font-family', 'font-size', 'font-style', 'font-weight',
  'height', 'line-height', 'margin', 'margin-bottom', 'margin-left', 'margin-right',
  'margin-top', 'max-width', 'min-width', 'padding', 'padding-bottom',
  'padding-left', 'padding-right', 'padding-top', 'text-align', 'text-decoration',
  'vertical-align', 'white-space', 'width', 'word-break', 'overflow-wrap'
]);

const TABLE_STYLE_PROPERTIES = new Set([
  ...BASE_STYLE_PROPERTIES, 'border-collapse', 'border-spacing', 'table-layout'
]);

const UNSAFE_STYLE_VALUE = /url\s*\(|expression\s*\(|javascript\s*:|@import|-moz-binding|behavior\s*:/i;

const DOMPURIFY_CONFIG = {
  ALLOWED_TAGS,
  ALLOWED_ATTR,
  ALLOW_ARIA_ATTR: false,
  ALLOW_DATA_ATTR: false,
  FORBID_TAGS: ['embed', 'form', 'iframe', 'input', 'link', 'meta', 'object', 'script', 'style', 'svg'],
  SANITIZE_DOM: true,
  SANITIZE_NAMED_PROPS: true
};

const cleanStyles = (root) => {
  root.querySelectorAll('[style]').forEach((element) => {
    const allowedProperties = ['TABLE', 'THEAD', 'TBODY', 'TFOOT', 'TR', 'TH', 'TD', 'COL', 'COLGROUP'].includes(element.tagName)
      ? TABLE_STYLE_PROPERTIES
      : BASE_STYLE_PROPERTIES;

    Array.from(element.style).forEach((property) => {
      const value = element.style.getPropertyValue(property);
      if (!allowedProperties.has(property.toLowerCase()) || UNSAFE_STYLE_VALUE.test(value)) {
        element.style.removeProperty(property);
      }
    });

    if (!element.getAttribute('style')?.trim()) element.removeAttribute('style');
  });
};

const cleanClasses = (root, source) => {
  root.querySelectorAll('[class]').forEach((element) => {
    const classes = element.className.split(/\s+/).filter(Boolean);
    const nextClasses = source === 'paste'
      ? classes.filter((className) => SAFE_CLASSES.has(className))
      : classes.filter((className) => /^[a-zA-Z0-9_-]+$/.test(className));

    if (nextClasses.length) element.className = nextClasses.join(' ');
    else element.removeAttribute('class');
  });
};

const normalizeLinks = (root) => {
  root.querySelectorAll('a[href]').forEach((link) => {
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');
  });
};

const normalizeImages = (root) => {
  root.querySelectorAll('img').forEach((image) => {
    image.removeAttribute('srcset');
    image.removeAttribute('usemap');
    if (!image.hasAttribute('alt')) image.setAttribute('alt', '');
  });
};

const normalizeTables = (root) => {
  root.querySelectorAll('table').forEach((table) => {
    table.classList.add('pmpi-rich-table');
    table.setAttribute('data-pmpi-table', 'true');

    table.querySelectorAll('td, th').forEach((cell) => {
      const align = cell.getAttribute('align')?.toLowerCase();
      if (['left', 'center', 'right', 'justify'].includes(align)) {
        cell.style.textAlign = align;
      }
      cell.removeAttribute('align');
      if (cell.tagName === 'TH' && !cell.hasAttribute('scope')) {
        cell.setAttribute('scope', cell.parentElement?.rowIndex === 0 ? 'col' : 'row');
      }
    });
  });
};

const postProcess = (root, source) => {
  cleanStyles(root);
  cleanClasses(root, source);
  normalizeLinks(root);
  normalizeImages(root);
  normalizeTables(root);
  return root;
};

export const sanitizeRichTextHtml = (dirtyHtml, { source = 'stored' } = {}) => {
  if (!dirtyHtml) return '';
  const sanitized = DOMPurify.sanitize(String(dirtyHtml), DOMPURIFY_CONFIG);
  if (typeof document === 'undefined') return sanitized;

  const container = document.createElement('div');
  container.innerHTML = sanitized;
  postProcess(container, source);
  return container.innerHTML;
};

export const sanitizeRichTextElement = (root, { source = 'stored' } = {}) => {
  if (!root) return root;
  // The root is the trusted React-owned editor. DOMPurify must still sanitize
  // all of its descendants, but removing this root attribute permanently
  // disables editing because React may not re-apply an unchanged prop.
  const rootContentEditable = root.getAttribute('contenteditable');
  DOMPurify.sanitize(root, { ...DOMPURIFY_CONFIG, IN_PLACE: true });
  if (rootContentEditable !== null) {
    root.setAttribute('contenteditable', rootContentEditable);
  }
  return postProcess(root, source);
};
